import { StyleSheet, css } from 'aphrodite'
import { AnimatePresence, motion } from 'framer-motion'
import type React from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { useEffect, useRef, useState } from 'react'
import ScrollToTop from '../components/ScrollToTop'
import Header from '../components/navigation/Header'
import Sidebar from '../components/navigation/Sidebar'
import Topbar from '../components/navigation/Topbar'

const initialPageVariants = {
  // No transition for initial load
  initial: { opacity: 1, x: 0 },
  animate: { opacity: 1, x: 0 },
}

const pageVariants = {
  forward: {
    initial: { x: '100%', opacity: 0 },
    animate: { x: 0, opacity: 1, transition: { x: { duration: 0.3 } } },
  },
  backward: {
    initial: { x: '-100%', opacity: 0 },
    animate: { x: 0, opacity: 1, transition: { duration: 0.3 } },
  },
}

const Layout: React.FC = () => {
  const location = useLocation()
  const firstMount = useRef(true)
  const [navigationDirection, setNavigationDirection] = useState<
    'forward' | 'backward'
  >('forward')

  // Choose variants based on whether it's the first render
  const variants = firstMount.current
    ? initialPageVariants
    : pageVariants[navigationDirection]

  // After first render, mark as not first mount anymore
  if (firstMount.current) {
    firstMount.current = false
  }

  return (
    <div className={css(styles.app)}>
      <ScrollToTop />
      <Topbar />
      <Sidebar />
      <div className={css(styles.contentWrapper)}>
        <div className={css(styles.mobileHeader)}>
          <Header />
        </div>
        <div className={css(styles.outlet)}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
              className={css(styles.page)}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  app: {
    display: 'flex',
  },
  topbar: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: 64,
    zIndex: 1000,
    background: '#1F2A37',
    display: 'flex',
    alignItems: 'center',
    padding: '0 32px',
    boxSizing: 'border-box',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    '@media (max-width: 900px)': {
      display: 'none',
    },
  },
  sidebar: {
    position: 'fixed',
    top: 64,
    left: 0,
    width: 250,
    height: 'calc(100vh - 64px)',
    zIndex: 999,
    '@media only screen and (max-width: 1023px)': {
      display: 'none',
    },
  },
  contentWrapper: {
    width: '100%',
    position: 'relative',
    zIndex: 0,
    marginLeft: 250,
    marginTop: 64,
    '@media only screen and (max-width: 1023px)': {
      marginLeft: 0,
      marginTop: 0,
    },
  },
  mobileHeader: {
    display: 'none',
    '@media only screen and (max-width: 1023px)': {
      display: 'block',
    },
  },
  content: {},
  outlet: {
    position: 'relative',
    zIndex: 0,
  },
  page: {
    minHeight: 'calc(100vh - 63px)',
    backgroundColor: '#F9FAFB',
    position: 'relative',
    zIndex: 0,
  },
})

export default Layout
